<template>
  <div :class="['page-wrapper', { 'moving-variant': isMovingVariant }]">
    <section :class="['hero-panel', { 'moving-hero-panel': isMovingVariant }]">
      <div v-if="isMovingVariant" class="moving-hero-viewport">
        <img :src="heroImageSrc" class="hero-image moving-hero-image" alt="Battle League Gaming moving hero image">
      </div>
      <img v-else :src="heroImageSrc" class="hero-image" alt="Battle League Gaming hero image">
      <div class="hero-shadow"></div>
      <div class="overlay-image-wrapper">
        <img :src="require(`../../brand/${this.brandConfig.brand}/assets/logo-caption.png`)" class="overlay-image" alt="Battle League Gaming">
      </div>
    </section>

    <section :class="['content-panel', { 'moving-content-panel': isMovingVariant }]">
      <div class="intro-copy">
        {{ introCopy }}
      </div>

      <button class="accept-btn" @click="handleSubscription">
        {{ this.brandConfig.subscribeButton }}
      </button>

      <div v-if="outroCopy" class="outro-copy">
        {{ outroCopy }}
      </div>

      <section v-if="isMovingVariant" class="battlegrounds-section">
        <div class="battlegrounds-label">
          {{ this.brandConfig.battlegroundsLabel }}
        </div>
        <img :src="battlegroundGroupSrc" class="battlegrounds-group-image" alt="Battle League Gaming battlegrounds">
      </section>


      <div v-if="brandConfig.brand ==='blg-en' || brandConfig.brand ==='blg-am'" class="support-copy">
        {{ this.brandConfig.unsubHelpText }}
      </div>

      <div @click="onTsAndCsClicked" class="terms-and-conditions">
        {{ this.brandConfig.termsAndConditionsText }}
      </div>
    </section>
  </div>
</template>

<script>
import {mapState, mapMutations} from "vuex";
import {ref} from 'vue';
import OnevasService from "@/services/onevasService";
import CampaignService from "@/services/campaignService";
import {triggerGTMEvent} from "@/functions/addGTM";
import {ethiotelConfig} from "@/config";
import {v4 as guid} from "uuid";
import parseTransactionId from "@/functions/parseTransactionId";

const isLoading = ref(false);
const errorMessage = ref(null);

export default {
  data() {
    return {
      msisdn: undefined,
      correlationId: undefined,
      trxId: undefined,
      mcc: undefined,
      mnc: undefined,
      spId: undefined,
      serviceId: undefined,
      productId: undefined,
      authorization: undefined,
      redirectUrl: ethiotelConfig.redirectUrl,
      entryChannel: 'Web',
      campaignId: undefined,
    }
  },
  computed: {
    ...mapState(['brandConfig', 'msisdn']),
    isMovingVariant() {
      return this.$route?.path === '/moving';
    },
    heroImageSrc() {
      if (!this.brandConfig?.brand) {
        return '';
      }

      if (this.isMovingVariant) {
        return require(`../../brand/${this.brandConfig.brand}/assets/moving/animationBanner.jpg`);
      }

      return require(`../../brand/${this.brandConfig.brand}/assets/evina-img.webp`);
    },
    battlegroundGroupSrc() {
      return require(`../../brand/${this.brandConfig.brand}/assets/moving/BattlegroundGroup.png`);
    },
    subHeaderParts() {
      const text = this.brandConfig?.subHeaderText?.trim() || '';
      const parts = text.match(/[^.!?።]+[.!?።]?/g)?.map((part) => part.trim()).filter(Boolean) || [];

      if (!parts.length) {
        return {
          intro: '',
          outro: '',
        };
      }

      if (parts.length === 1) {
        return {
          intro: parts[0],
          outro: '',
        };
      }

      return {
        intro: parts[parts.length - 1],
        outro: parts.slice(0, -1).join(' '),
      };
    },
    introCopy() {
      return this.subHeaderParts.intro;
    },
    outroCopy() {
      return this.subHeaderParts.outro;
    }
  },
  methods: {
    ...mapMutations(['setMsisdn']),
    onTsAndCsClicked() {
      window.open(this.brandConfig.termsAndConditionsUrl, '_blank');
    },
    generateCorrelationId() {
      return guid().toString();
    },
    generateBasicAuth() {
      // Create the username:password string
      const credentials = `${ethiotelConfig.username}:${ethiotelConfig.password}`;
      // Convert to Base64
      const base64Credentials = btoa(credentials);
      // Return with "Basic " prefix
      return `Basic ${base64Credentials}`;
    },
    handleSearchUrlParams() {
      const urlParams = new URLSearchParams(window.location.search);
      const parsedTransactionId = parseTransactionId(urlParams.get('transaction_id'));
      // Extract all parameters
      this.msisdn = urlParams.get("msisdn") ?? null;
      this.correlationId = this.generateCorrelationId()
      this.trxId = this.correlationId;
      this.mcc = this.brandConfig?.mcc;
      this.mnc = this.brandConfig?.mnc;
      this.spId = this.brandConfig?.spId;
      this.serviceId = this.brandConfig?.serviceId;
      this.productId = this.brandConfig?.productId;
      this.authorization = this.generateBasicAuth();
      this.campaignId = urlParams.get("campaignId") ?? parsedTransactionId?.campaignId ?? this.brandConfig?.campaignId;
    },
    handleSubscriptionRedirect() {
      const redirectUrl = `${ethiotelConfig.redirectUrl}?correlatorId=${this.correlationId}&trxId=${this.trxId}&mcc=${this.mcc}&mnc=${this.mnc}&spId=${this.spId}&serviceId=${this.serviceId}&productId=${this.productId}&authorization=${this.authorization}&entryChannel=${this.entryChannel}&redirectURL=${this.brandConfig.redirectUrl}`;
      window.location.href = redirectUrl;
    },
    async handleSubscription() {
      triggerGTMEvent('subscribe_button_clicked');
      isLoading.value = true;
      errorMessage.value = null;

      try {
        triggerGTMEvent('user_onnet');
        const urlParams = new URLSearchParams(window.location.search);
        const parsedTransactionId = parseTransactionId(urlParams.get('transaction_id'));
        const msisdn = urlParams.get('msisdn') ?? urlParams.get('phone');

        const {
          clickIdParameterName,
          publisherIdParameterName,
        } = await CampaignService.getCampaignConfig(this.campaignId);


        const publisherId = urlParams.get(`${publisherIdParameterName}`) ?? urlParams.get('publisherId') ?? parsedTransactionId?.publisherId;
        const clickId = parsedTransactionId?.clickId ?? urlParams.get(`${clickIdParameterName}`) ?? urlParams.get('clickid') ?? urlParams.get('clickId') ?? urlParams.get('click_id') ?? urlParams.get('ofreqid') ?? urlParams.get('transaction_id') ?? urlParams.get('pr_mbp_clickID');


        await OnevasService.queueUserSubscription(this.correlationId, msisdn, this.serviceId, this.productId, this.campaignId, clickId, publisherId, undefined, undefined);

        console.log('User subscription queued successfully');

        if (msisdn) {
          const user = await OnevasService.getUsers(msisdn, this.brandConfig.productId);

          if (user?.Status === "Active") {
            const gameUrl = `${this.brandConfig.gameUrl}?msisdn=${msisdn}`;
            window.location.href = gameUrl;
            return;
          }
        }

        this.handleSubscriptionRedirect();
      } catch (error) {
        console.error('=== Subscription Error ===');
        console.error('Error:', error);
        console.error('Error response:', error.response);
        errorMessage.value = error.response?.data?.message || error.message || "An unknown error occurred.";
      } finally {
        isLoading.value = false;
      }
    },
    handleDecline() {
      window.history.back();
    }
  },
  async mounted() {
    triggerGTMEvent('landing_page_view');
    console.log('=== Landing Page Mounted ===');
    this.handleSearchUrlParams();
  }
}
</script>

<style scoped>
.page-wrapper {
  min-height: 100vh;
  background: #020202;
  color: #FFFFFF;
  display: flex;
  flex-direction: column;
}

.hero-panel {
  position: relative;
  width: 100%;
  overflow: hidden;
}

.moving-hero-panel {
  background: #020202;
}

.moving-hero-viewport {
  width: 100%;
  min-height: 64vh;
  max-height: 74vh;
  overflow: hidden;
}

.hero-image {
  width: 100%;
  min-height: 64vh;
  max-height: 74vh;
  object-fit: cover;
  object-position: center top;
  display: block;
}

.moving-hero-image {
  width: 190%;
  max-width: none;
  height: 100%;
  object-fit: cover;
  object-position: left center;
  animation: moving-banner-sweep 18s linear infinite;
  will-change: transform;
}

.hero-shadow {
  position: absolute;
  inset: 0;
  background:
      linear-gradient(180deg, rgba(0, 0, 0, 0.28) 0%, rgba(0, 0, 0, 0) 14%, rgba(0, 0, 0, 0.18) 58%, rgba(2, 2, 2, 0.96) 100%),
      linear-gradient(180deg, rgba(2, 2, 2, 0) 0%, rgba(2, 2, 2, 0.9) 92%, #020202 100%);
}

.overlay-image-wrapper {
  position: absolute;
  left: 50%;
  bottom: 7%;
  width: min(84%, 360px);
  transform: translateX(-50%);
  pointer-events: none;
  z-index: 2;
  bottom: 25%;
}

.overlay-image {
  width: 100%;
  height: auto;
  display: block;
  filter: drop-shadow(0 10px 20px rgba(0, 0, 0, 0.5));
}

.moving-variant .overlay-image-wrapper {
  bottom: 34%;
}

.content-panel {
  width: 100%;
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  margin-top: -8rem;
  padding: 0 1.15rem 2rem;
  position: relative;
  z-index: 3;
}

.moving-content-panel {
  gap: 1.75rem;
  margin-top: -9.5rem;
  padding-top: 0.75rem;
}

.intro-copy,
.outro-copy,
.support-copy,
.terms-and-conditions {
  text-align: center;
}

.intro-copy {
  max-width: 20rem;
  color: #C4C4C4;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.45);
}

.accept-btn {
  width: 353px;
  max-width: 100%;
  height: 91.127px;
  border: none;
  border-radius: 5px;
  background: #B3F50D;
  color: #000;
  cursor: pointer;
  font-family: "Clash Display", sans-serif;
  font-size: 25.214px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  padding: 0 1rem;
  box-shadow: 0 14px 30px rgba(0, 0, 0, 0.35);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.accept-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 18px 34px rgba(0, 0, 0, 0.4);
}

.outro-copy {
  max-width: 21rem;
  color: #C4C4C4;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
}

.battlegrounds-section {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
}

.battlegrounds-label {
  width: 208px;
  max-width: 100%;
  color: #C4C4C4;
  text-align: center;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 100%;
}

.battlegrounds-group-image {
  width: min(100%, 360px);
  height: auto;
  display: block;
}

.support-copy {
  max-width: 18rem;
  margin-top: auto;
  color: #B3F50D;
  font-family: Inter, sans-serif;
  font-size: 10px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
}

.terms-and-conditions {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.8rem;
  text-decoration: underline;
  cursor: pointer;
}

@keyframes moving-banner-sweep {
  0% {
    transform: translateX(0);
  }

  100% {
    transform: translateX(-47.5%);
  }
}

@media (min-width: 768px) {
  .hero-image {
    min-height: 70vh;
  }

  .moving-hero-viewport {
    min-height: 70vh;
  }

  .moving-variant .overlay-image-wrapper {
    bottom: 30%;
    width: min(54%, 320px);
  }

  .content-panel {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }

  .moving-content-panel {
    margin-top: -8.5rem;
  }

  .battlegrounds-group-image {
    width: min(100%, 420px);
  }
}
</style>
